"use client";

import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import clsx from "clsx";
import Button from "@/components/common/Button";
import TextField from "@/components/common/TextField";
import Select from "@/components/common/Select";
import ScrollReveal from "@/components/common/ScrollReveal";
import { countries } from "@/lib/constants";
import { getApiErrorMessage } from "@/lib/apiError";
import {
  RECAPTCHA_FAILED_MESSAGE,
  getRecaptchaToken,
  isRecaptchaFailure,
} from "@/lib/recaptcha";
import { ContactFormData } from "@/types/constants_type";

/** Interest options — kept in sync with the navigation menu (Header.tsx).
 *  Exported so the Contact page form (ContactForm) reuses the same lists. */
export const PRODUCT_OPTIONS: string[] = [
  "ECQ Q-SUITE",
  "HackGrid - Cyber Range",
  "SHRED",
  "DarkSight - Threat Intelligence",
];

export const SERVICE_OPTIONS: string[] = [
  "Scenario-based Penetration Test",
  "Adversary Simulation",
  "Web Application Penetration Test",
  "Mobile Application Penetration Test",
  "Compromise Assessment",
  "Application Security Analysis",
  "Secure Code Review",
  "PCI Compliance",
  "ISO Standard",
  "Local Regulatory",
];

/** Company name lives in UI state only (folded into `message` on submit). */
interface RequestDemoValues extends ContactFormData {
  companyName: string;
}

interface RequestDemoFormProps {
  /** Recipient mailbox; defaults to the sales inbox. */
  mailTo?: string;
  /** Used only when `onSuccess` is not provided (standalone page mode). */
  successHref?: string;
  /** Called after a successful submit (modal mode shows the thank-you view). */
  onSuccess?: () => void;
  /** Called when Cancel is pressed (modal mode closes the popup). */
  onCancel?: () => void;
}

function PlusMinusIcon({ open }: { open: boolean }) {
  return (
    <span className="relative flex size-6 items-center justify-center text-primary">
      <span className="absolute h-[2px] w-[14px] rounded-full bg-current" />
      <span
        className={clsx(
          "absolute h-[14px] w-[2px] rounded-full bg-current transition-transform duration-300",
          open ? "scale-y-0" : "scale-y-100"
        )}
      />
    </span>
  );
}


/**
 * Expandable interest group (Figma "Multi Select" + "Select-Cell").
 * Collapsed: dark surface-3/20 row with a red +/- and the group title.
 * Expanded: a multi-select checklist; selected cells turn primary with a
 * minus glyph (hover on unselected reveals a plus), matching Figma Form.
 */
export function InterestGroup({
  title,
  options,
  selected,
  onToggleItem,
}: {
  title: string;
  options: string[];
  selected: Set<string>;
  onToggleItem: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="w-full overflow-hidden rounded-[4px] bg-table-stripe/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-6 px-6 py-4 cursor-pointer"
      >
        <PlusMinusIcon open={open} />
        <span className="body-1-heavy text-text-highlight">{title}</span>
      </button>

      <div
        className={clsx(
          "grid transition-[grid-template-rows] duration-300 ease-out",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col pb-2 max-h-[168px] overflow-y-auto ecq-scrollbar">
            {options.map((option) => {
              const isSelected = selected.has(option);
              return (
                <button
                  key={option}
                  type="button"
                  onClick={() => onToggleItem(option)}
                  aria-pressed={isSelected}
                  className={clsx(
                    // 60px each side is a desktop-only indent: inside the modal a
                    // 390px phone leaves 294px of content width, so 2 x 60 = 120px
                    // of padding would squeeze the option label into 174px.
                    "group flex w-full items-center justify-between gap-4 px-6 sm:px-[60px] py-3 text-left transition-colors duration-200 cursor-pointer",
                    isSelected ? "text-primary" : "text-white hover:bg-surface-2"
                  )}
                >
                  <span
                    className={clsx(
                      "text-[16px] tracking-[0.5px]",
                      isSelected
                        ? "body-1-heavy"
                        : "font-[var(--font-avenir)] font-normal leading-7"
                    )}
                  >
                    {option}
                  </span>
                  <span
                    className={clsx(
                      "flex-shrink-0 transition-opacity duration-200",
                      isSelected
                        ? "opacity-100"
                        : "opacity-0 group-hover:opacity-100"
                    )}
                  >
                    {/* Figma: selected → red minus, hover (unselected) → plus */}
                    <PlusMinusIcon open={isSelected} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * "Contact Us" demo-request form (Figma 1583:52062) used by the
 * "Request a Demo" popup (RequestDemoModal). Two-column layout, a Company Name
 * field, and two expandable multi-select interest groups. Submits to the same
 * /users/contact endpoint as the contact form — interests are serialized into
 * `inquiry` and Company Name is folded into `message`.
 */
export default function RequestDemoForm({
  mailTo = "sales.th@e-cq.net",
  successHref = "/contact/thank-you",
  onSuccess,
  onCancel,
}: RequestDemoFormProps) {
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
    new Set()
  );
  const [selectedServices, setSelectedServices] = useState<Set<string>>(
    new Set()
  );

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<RequestDemoValues>({
    mode: "onBlur",
    defaultValues: {
      name: "",
      email: "",
      companyName: "",
      country: "",
      inquiry: "",
      message: "",
    },
  });

  const toggleFromSet = (
    setState: React.Dispatch<React.SetStateAction<Set<string>>>,
    value: string
  ) => {
    clearErrors("inquiry");
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const onSubmit = async (data: RequestDemoValues) => {
    const parts: string[] = [];
    if (selectedProducts.size > 0) {
      parts.push(`Our Product: ${Array.from(selectedProducts).join(", ")}`);
    }
    if (selectedServices.size > 0) {
      parts.push(`Our Services: ${Array.from(selectedServices).join(", ")}`);
    }
    const inquiry = parts.join(" | ");

    // The interest groups are local state, not registered fields, so they need
    // their own required check — otherwise `inquiry` posts as an empty string.
    if (!inquiry) {
      setError("inquiry", {
        type: "required",
        message: "Please select at least one product or service",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    const message = data.companyName.trim()
      ? `Company: ${data.companyName.trim()}\n\n${data.message}`
      : data.message;

    /** Posts the form with a brand-new token — the backend refuses a reused
     *  or >120 s-old one with the same "Invalid reCAPTCHA" error. */
    const post = async () => {
      const token = await getRecaptchaToken();

      const response = await fetch(
        "https://diana.e-cq.net/api/v1/users/contact",
        {
          method: "POST",
          cache: "no-cache",
          headers: { "Content-Type": "application/json" },
          redirect: "follow",
          referrerPolicy: "no-referrer",
          body: JSON.stringify({
            name: data.name,
            email: data.email,
            country: data.country,
            inquiry,
            mailTo,
            message,
            recaptchaResponse: token,
          }),
        }
      );

      return response.json();
    };

    try {
      let result = await post();

      // Captcha refused (often a stale token) → retry once, never the same token.
      if (isRecaptchaFailure(result)) {
        result = await post();
      }

      if (result?.status === "success") {
        reset();
        if (onSuccess) onSuccess();
        else router.push(successHref);
      } else if (isRecaptchaFailure(result)) {
        // Hide the backend's raw "Oops! Invalid reCAPTCHA" wording.
        setSubmitError(RECAPTCHA_FAILED_MESSAGE);
      } else {
        // Show the backend's validation/error message instead of a generic one.
        setSubmitError(getApiErrorMessage(result));
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setSubmitError(
        // A missing script / empty token throws out of getRecaptchaToken().
        isRecaptchaFailure(err)
          ? RECAPTCHA_FAILED_MESSAGE
          : "Something went wrong! Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ScrollReveal direction="none" delay={150}>
      <div
        className={clsx(
          "relative w-full flex flex-col items-center gap-6",
          "bg-surface-alt rounded-[16px]",
          "drop-shadow-[-8px_8px_11px_rgba(0,0,0,0.25)]",
          "px-5 py-6 sm:px-8 sm:py-10"
        )}
      >
        <h3 className="headline-2 text-text-highlight text-center w-full">
          Contact Us
        </h3>

        {submitError && (
          <div className="w-full p-3 rounded-lg bg-primary-600/10 border border-primary-600 text-primary-600">
            <p className="text-center font-normal text-[14px] leading-[20px]">
              {submitError}
            </p>
          </div>
        )}

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-6"
          noValidate
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
            <TextField
              label="Name"
              required
              placeholder=""
              errorMessage={errors.name?.message}
              {...register("name", {
                required: "This is required field",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters",
                },
              })}
            />
            <TextField
              label="Email"
              required
              placeholder=""
              errorMessage={errors.email?.message}
              {...register("email", {
                required: "This is required field",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              })}
            />

            <TextField
              label="Company Name"
              placeholder=""
              {...register("companyName")}
            />

            <div className="flex flex-col items-start w-full">
              <div className="flex items-center px-4 leading-7 text-[16px] tracking-[0.5px] font-[var(--font-avenir)] text-neutral-300">
                <span>Country</span>
                <span>*</span>
              </div>
              <Controller
                name="country"
                control={control}
                rules={{ required: "This is required field" }}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onChange={field.onChange}
                    placeholder=""
                    error={errors.country?.message}
                    items={countries.map((c) => ({
                      value: c.value,
                      label: c.label,
                    }))}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex flex-col items-start w-full gap-4">
            <p className="px-4 leading-7 text-[16px] tracking-[0.5px] font-[var(--font-avenir)] text-neutral-300">
              Select what you&rsquo;re interested in
            </p>
            <InterestGroup
              title="Our Product"
              options={PRODUCT_OPTIONS}
              selected={selectedProducts}
              onToggleItem={(value) => toggleFromSet(setSelectedProducts, value)}
            />
            <InterestGroup
              title="Our Services"
              options={SERVICE_OPTIONS}
              selected={selectedServices}
              onToggleItem={(value) => toggleFromSet(setSelectedServices, value)}
            />
            {errors.inquiry?.message && (
              <div className="flex items-center px-4">
                <p className="text-primary-600 text-[12px] leading-5 tracking-[0.25px] font-[var(--font-avenir)]">
                  {errors.inquiry.message}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col items-start w-full">
            {/* Asterisk to match Name/Email/Country — the field is registered
                `required`, so the label has to say so too. */}
            <div className="flex items-center px-4 leading-7 text-[16px] tracking-[0.5px] font-[var(--font-avenir)] text-neutral-300">
              <span>Message</span>
              <span>*</span>
            </div>
            <div
              className={clsx(
                // mx-4, not px-4 — the underline is on this element and has to stay
                // on the same 16px inset as the label/value/error text.
                "flex items-center self-stretch mx-4 py-1 border-b transition-colors",
                errors.message ? "border-primary-600" : "border-neutral-500"
              )}
            >
              <textarea
                {...register("message", {
                  required: "This is required field",
                  minLength: {
                    value: 5,
                    message: "Message must be at least 5 characters",
                  },
                })}
                rows={2}
                placeholder=""
                className={clsx(
                  "flex-1 min-w-0 bg-transparent outline-none border-none",
                  "font-[var(--font-avenir)] text-text-default text-[16px] leading-7 tracking-[0.5px]",
                  "placeholder:text-neutral-500 resize-none"
                )}
              />
            </div>
            {errors.message && (
              <p className="px-4 mt-2 text-primary-600 text-[12px] leading-5 tracking-[0.25px] font-[var(--font-avenir)]">
                {errors.message.message}
              </p>
            )}
          </div>

          <p className="body-1 text-text-default text-center w-full">
            For urgent support, please contact our local hotline.
          </p>

          {/* Stacked below sm: the phone media query gives every full-size pill a
              200px floor, so two of them side by side need 424px and were being
              clipped at both ends inside a 318px row on a 390px screen. Only one
              pill fits per line there. */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 w-full pt-2">
            {/* Cancel is the same V1 pill as Send Message in its neutral colour
                (--ghost), matching the cookie banner's Reject/Accept pair: same
                component, same DOM, only the accent differs. It replaces a
                variant="text" label that rendered 53×28px next to a 189×64px
                pill — below the 44px touch-target floor and 36px off the other
                button's centre line. height 64 mirrors Send Message so the two
                share a baseline. */}
            <Button
              primary
              themeProp="dark"
              type="button"
              height={64}
              onClick={onCancel ?? (() => router.push("/"))}
              disabled={isSubmitting}
              customClassName="ecq-cta--ghost"
            >
              Cancel
            </Button>
            <Button
              variant="fill"
              color="primary"
              type="submit"
              height={64}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </Button>
          </div>
        </form>
      </div>
    </ScrollReveal>
  );
}
